import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import analyzeExcelHeadersTool from 'common/src/modules/shared/copilot/tools/analyzeExcelHeadersTool/analyzeExcelHeadersTool';
import readValuesFromExcelTool from 'common/src/modules/shared/copilot/tools/readValuesFromExcelTool/readValuesFromExcelTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {SequenceCardScratchpadState} from '../../scratchpad/sequenceCardScratchpad.types';
import getOrCreateSequenceCard from '../../utils/sequence/getOrCreateSequenceCard';
import addRegularOutputTool from './addRegularOutputTool';
import editRegularOutputTool from './editRegularOutputTool';
import deleteRegularOutputsTool from './deleteRegularOutputsTool';
import setRepeatCycleTool from './setRepeatCycleTool';

const cardSpecificInstructions = `### What is a Sequence card
A Sequence card delivers outputs to users in order, with configurable frequency caps per output. It controls how many times a user sees each step before advancing to the next. The fallback output is selected when the user cannot be identified or their profile cannot be retrieved.

### Sequence card rules
- Must have at least 1 regular output.

### Special outputs (Fallback and Last) -- IMPORTANT
- The card has two special built-in outputs that CANNOT be directly added or deleted:
  1. **Fallback output** - automatically present, handles unidentified users. It cannot be added, deleted, renamed, or configured in any way.
  2. **Last output** - controls when the sequence stops repeating. It is NOT managed directly -- it is automatically added/removed by the setRepeatCycle tool.
- To make the sequence repeat a limited number of times: use setRepeatCycle with mode 'finite'. This automatically adds a Last output.
- To make the sequence loop forever (default for new cards): use setRepeatCycle with mode 'infinite'. This automatically removes the Last output.
- If the user asks to "delete the Last output", they likely want the sequence to repeat without end -- use setRepeatCycle with mode 'infinite'.
- DO NOT try to add Fallback or Last outputs using addRegularOutput -- those are system-managed.

### Property Dependencies
- All properties (outputs, repeat cycle, card name, description) are independent — none depend on another being set first.`;

export default function addOrUpdateSequenceCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, SequenceCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const preProcessResult = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateSequenceCard,
    );

    if (preProcessResult.ok === false) {
      return toolError(preProcessResult.error);
    }

    const {promptToUse, normalizedCardId, card} = preProcessResult;

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateSequenceCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.SEQUENCE]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.SEQUENCE,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getFilesTool(),
        getCardDetailsTool(),
        analyzeExcelHeadersTool(),
        readValuesFromExcelTool(),

        addRegularOutputTool(),
        editRegularOutputTool(),
        deleteRegularOutputsTool(),
        setRepeatCycleTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateSequenceCard,
    description:
      'Add or update a Sequence card. Delivers outputs to users in order, with configurable frequency caps per output and repeat cycle settings. Supports reading values from Excel.',
    displayName: 'Working on sequence card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
